// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ArrowGo from "@eisinc/XDL/Icons/ArrowGo";
import { xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import HTMLEllipsis from "react-lines-ellipsis/lib/html";
import styled, { css } from "styled-components";

export interface BlogTileStyleProps {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    summaryWrapper?: InjectableCss;
    arrowLink?: InjectableCss;
}

export const blogTileStyles: BlogTileStyleProps = {
    wrapper: {
        css: css`
            flex-direction: column;
            border-top: 3px solid ${getColor("primary.main")};
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);

            flex: 0 1 clamp(310px, calc((100% - 150px) / 4), 310px);

            ${xScreen("lte", "md")} {
                flex: 0 1 clamp(290px, calc((100% - 100px) / 3), 310px);
            }

            ${xScreen("lte", "sm")} {
                flex: 1 1 clamp(290px, 50%, 310px);
            }
        `,
    },
    title: {
        size: "18px",
        lineHeight: "22px",
        color: "text.main",
        css: css`
            letter-spacing: 0.3px;
            text-align: left;
            height: 44px;
            overflow: hidden;
            margin-bottom: 15px;
            font-weight: bold;
        `,
    },
    summaryWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
};

export interface BlogModel {
    title?: string;
    image?: string;
    summary?: string;
    urlSegment?: string;
}

export interface BlogTileProps {
    blog: BlogModel;
}

type OwnProps = BlogTileProps;

const BlogTile: React.FC<OwnProps> = ({ blog }) => {
    return (
        <Clickable href={blog.urlSegment} {...blogTileStyles.wrapper}>
            <StyledWrapper
                css={css`
                    background-image: url(${blog.image});
                    background-repeat: no-repeat;
                    background-size: 100% 100%;
                    background-color: ${getColor("common.ui2")};
                    height: 155px;
                    width: 100%;
                `}
            />
            <StyledWrapper
                css={css`
                    width: 100%;
                    padding: 10px 10px 5px;
                    height: calc(100% - 155px);
                    display: flex;
                    flex-direction: column;
                `}
            >
                <Typography {...blogTileStyles.title} as="h3">
                    {blog.title}
                </Typography>
                <StyledWrapper {...blogTileStyles.summaryWrapper}>
                    <HTMLEllipsis unsafeHTML={blog.summary} maxLine="3" ellipsisHTML="..." basedOn="letters" />
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: flex;
                        justify-content: flex-end;
                    `}
                >
                    <Icon src={ArrowGo} size={35} color="primary.main" />
                </StyledWrapper>
            </StyledWrapper>
        </Clickable>
    );
};

export default BlogTile;
